// @greenlight-spec: 11-observability.md#proxy-attribution-coverage
// @greenlight-spec: 09-data-brokering.md#user-actor-token
import { z } from 'zod';
/**
 * Why an app's proxy calls do or don't name a person.
 *
 * A single audit row cannot answer this: a call with no user attached looks
 * identical whether a background job made it (correct) or an app serving a
 * signed-in user never forwarded `X-Greenlight-Actor-Token` (a gap). The
 * distinguishing fact lives outside the row — whether the app served any
 * signed-in user in the same window, from the `/auth/check` usage rollup.
 * Classification is therefore per-app-per-window, never per-row.
 */
export const AttributionStatusSchema = z.enum([
    // Every proxy call named a user.
    'attributed',
    // Some named a user, some didn't — normal for an app that also does background work.
    'partial',
    // No proxy call named a user, and the app served signed-in users in the window.
    // The actionable case: a builder agent fixes it by forwarding the actor token.
    'gap',
    // No proxy call named a user, and no signed-in user activity was recorded.
    // Consistent with background-only work, which is correct by design — so this
    // is stated as consistency, not as a verdict.
    'background_only',
]);
export const AppAttributionCoverageSchema = z.object({
    app_id: z.string().uuid(),
    app_name: z.string(),
    app_slug: z.string(),
    status: AttributionStatusSchema,
    proxy_calls: z.number().int().nonnegative(),
    user_attributed_calls: z.number().int().nonnegative(),
    /** Whole percent of proxy calls naming a user; 0 when there were no calls. */
    coverage_percent: z.number().int().min(0).max(100),
    last_user_attributed_at: z.string().datetime({ offset: true }).nullable(),
    /** Whether the usage rollup recorded any signed-in user for this app in the window. */
    served_signed_in_users: z.boolean(),
});
export const AttributionCoverageSchema = z.object({
    window_days: z.number().int().positive(),
    /** Apps with at least one proxy call in the window, worst status first. */
    apps: z.array(AppAttributionCoverageSchema),
    /**
     * Apps with proxy traffic in the window, before `limit` truncated `apps` —
     * so a truncated list reads as truncated rather than as the whole picture.
     */
    total_apps_with_proxy_traffic: z.number().int().nonnegative(),
});
//# sourceMappingURL=audit-attribution.js.map